import { useState, useEffect, useCallback } from "react";

import { useToast } from "@/hooks/useToast";

import { isUserAuthenticated } from "@/middleware/auth";

import { ChatEntity } from "../types";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import {
  getAIChatsFromLocalDB,
  getChatsWithoutHubIdFromLocalDB,
  getP2PChatsFromLocalDB,
  getGroupChatsFromLocalDB,
} from "../services/chats";
import { ChatType } from "@/server/types/chat";

interface UsePaginatedChatOptions {
  searchTerm: string;
  withHubId?: boolean;
  chatType?: ChatType.P2P | ChatType.GROUP;
  count?: number; // Results per page
}

export function usePaginatedChatsFromLocalDB({
  count = 50,
  withHubId,
  chatType,
  searchTerm,
}: UsePaginatedChatOptions) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [results, setResults] = useState<ChatEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const fetchResults = useCallback(
    async (newOffset: number, append: boolean) => {
      setIsLoading(true);

      // extra check if auth is present
      const userFingerPrint = isUserAuthenticated();

      if (!userFingerPrint) {
        return;
      }

      try {
        // TO DO => Implement fuzzy search on indexxed db
        // to handle large data sets
        //
        let chats;
        if (withHubId) {
          chats = await getAIChatsFromLocalDB();
        } else if (chatType === ChatType.P2P) {
          chats = await getP2PChatsFromLocalDB();
        } else if (chatType === ChatType.GROUP) {
          chats = await getGroupChatsFromLocalDB();
        } else {
          chats = await getChatsWithoutHubIdFromLocalDB();
        }

        const fuse = new Fuse(chats, {
          keys: ["name"],
          includeScore: true,
          threshold: 0.4,
          minMatchCharLength: 1,
        });

        const response = fuse
          .search(searchTerm)
          .map((r) => r.item)
          .sort((a, b) => {
            const dateA = a.lastMessageTimestamp
              ? new Date(a.lastMessageTimestamp).getTime()
              : null;
            const dateB = b.lastMessageTimestamp
              ? new Date(b.lastMessageTimestamp).getTime()
              : null;

            if (dateA === null && dateB === null) {
              return 0;
            }
            if (dateA === null) {
              return 1;
            }
            if (dateB === null) {
              return -1;
            }

            return dateB - dateA;
          })
          .slice(newOffset, newOffset + count);

        if (response.length < count) {
          setHasMore(false); // No more data available
        }
        if (append) {
          setResults((prev) => [...prev, ...response]); // Append results
        } else {
          setResults(response); // Replace results for a fresh search
        }
      } catch (error) {
        toast({
          duration: 1500,
          description:
            error instanceof Error ? error.message : t("cannotLoadChats"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, searchTerm],
  );

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setResults([]);
      setHasMore(false);
      return;
    }

    // Reset for a new search
    setOffset(0);
    setHasMore(true);
    fetchResults(0, false);
  }, [searchTerm, fetchResults]);

  const loadMore = () => {
    const newOffset = offset + count;
    setOffset(newOffset);
    fetchResults(newOffset, true);
  };

  return {
    isLoading,
    hasMore,
    results,
    loadMore,
  };
}
